import React from 'react';
import { OpeningHoursDayInDTO, OpeningModeEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ValidationError } from 'yup';
import ValidationDiv from '@utils/validation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage';
import InputHour from '@components/obsolete/Form/InputHour/InputHour';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';

interface ComponentProps {
  day: OpeningHoursDayInDTO;
  dayIndex: number;
  validationErrors?: ValidationError[];
  validationPrefix?: string;
  onOpen: () => void;
  onClose: () => void;
  onToggleAllDay: () => void;
  onAddInterval: () => void;
  onRemoveInterval: (index: number) => void;
  onChangeSince: (index: number, value: string) => void;
  onChangeTill: (index: number, value: string) => void;
  onGenerate: () => void;
}

const ALL_DAY_INTERVAL = { since: '00:00', till: '24:00' };

const OpeningHoursDayRow: React.FC<ComponentProps> = ({
  day,
  dayIndex,
  validationErrors,
  validationPrefix = '',
  onOpen,
  onClose,
  onToggleAllDay,
  onAddInterval,
  onRemoveInterval,
  onChangeSince,
  onChangeTill,
  onGenerate,
}) => {
  const { tr } = useTranslationLgs();
  const isClosed = day.openingMode === OpeningModeEnum.CLOSED;
  const isAllDay = day.openingMode === OpeningModeEnum.OPEN;
  const intervals = isAllDay ? [ALL_DAY_INTERVAL] : day.openingHours?.length ? day.openingHours : [{ since: '', till: '' }];

  // ValidationDiv renders an empty box when there is no error, which would add gap slots and bloat the day spacing — render it only on a real error.
  const hasError = (path: string) => (validationErrors ?? []).some(e => e.path === path);

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between pb-2">
        <span className="text-base font-medium text-content-primary">{tr(`TimeIntervalComponent.days${day.day}`, day.day)}</span>
        {isClosed && (
          <RingilButton mode="link" label={tr('LocationOpeningHours.generate', 'Generate')} onClick={onGenerate} dataTestId={`location-openingHours-generate-${day.day}`} />
        )}
      </div>

      {isClosed ? (
        <COInlineMessage
          variant="info"
          buttonsRight
          buttons={<RingilButton mode="outlined" label={tr('LocationOpeningHours.open', 'Open')} onClick={onOpen} dataTestId={`location-openingHours-open-${day.day}`} />}
          dataTestId={`location-openingHours-closed-${day.day}`}>
          <p>{tr('LocationOpeningHours.closedDot', 'Closed.')}</p>
        </COInlineMessage>
      ) : (
        <div className="flex flex-col gap-4">
          {intervals.map((hours, index) => {
            const isFirst = index === 0;
            return (
              <div key={index} className="flex flex-col gap-1">
                <div className="flex items-end gap-6">
                  <div className="flex flex-1 items-center gap-4">
                    <div className="flex gap-2">
                      <div className="w-[115px] [&_input]:w-full">
                        <InputHour value={hours.since} disabled={isAllDay} readOnly={isAllDay} onChange={v => onChangeSince(index, v)} dataTestId={`location-openingHours-${day.day}-${index}-since`} />
                      </div>
                      <div className="w-[115px] [&_input]:w-full">
                        <InputHour value={hours.till} disabled={isAllDay} readOnly={isAllDay} onChange={v => onChangeTill(index, v)} dataTestId={`location-openingHours-${day.day}-${index}-till`} />
                      </div>
                    </div>
                    {isFirst && (
                      <RingilInputSwitch label={tr('LocationOpeningHours.allDay', 'All day')} value={isAllDay} onChange={() => onToggleAllDay()} dataTestId={`location-openingHours-allDay-${day.day}`} />
                    )}
                  </div>
                  {isFirst ? (
                    <RingilButton mode="outlined" label={tr('LocationOpeningHours.setClosed', 'Closed')} onClick={onClose} dataTestId={`location-openingHours-close-${day.day}`} />
                  ) : (
                    <RingilButton mode="table" icon={<SvgDelete />} onClick={() => onRemoveInterval(index)} dataTestId={`location-openingHours-remove-${day.day}-${index}`} />
                  )}
                </div>
                {hasError(`${validationPrefix}openingHoursDays[${dayIndex}].openingHours[${index}]`) && (
                  <ValidationDiv errors={validationErrors} path={`${validationPrefix}openingHoursDays[${dayIndex}].openingHours[${index}]`} />
                )}
              </div>
            );
          })}
          {hasError(`${validationPrefix}openingHoursDays[${dayIndex}].intervalError`) && (
            <ValidationDiv errors={validationErrors} path={`${validationPrefix}openingHoursDays[${dayIndex}].intervalError`} />
          )}
          {!isAllDay && (
            <div>
              <RingilButton mode="link" icon={<SvgPlus />} label={tr('LocationOpeningHours.addWorkingHours', 'Add working hours')} onClick={onAddInterval} dataTestId={`location-openingHours-add-${day.day}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OpeningHoursDayRow;
